import { useEffect, useState } from "react";
import api, { fmtDate, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CategoryPicker, useCategories } from "@/components/CategoriesDialog";
import { CalendarPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BLANK = { name: "", start_date: "", end_date: "", category_names: [], employee_ids: [] };

function HolidayDialog({ open, onOpenChange, onSaved }) {
  const [cats] = useCategories();
  const [emps, setEmps] = useState([]);
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(BLANK); api.get("/employees").then((r) => setEmps((r.data || []).filter((e) => e.active !== false && e.worker_type !== "rct"))).catch(() => {}); } }, [open]);
  const toggle = (id) => setF((p) => ({ ...p, employee_ids: p.employee_ids.includes(id) ? p.employee_ids.filter((x) => x !== id) : [...p.employee_ids, id] }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/company-holidays", { ...f, end_date: f.end_date || f.start_date });
      const unpaid = data.applied.filter((a) => a.unpaid_days > 0).length;
      toast.success(`Added to ${data.applied.length} staff${unpaid ? ` (${unpaid} without enough leave, booked as unpaid)` : ""}${data.skipped.length ? `, ${data.skipped.length} skipped` : ""}`);
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="company-holiday-dialog">
        <DialogHeader><DialogTitle>Add company holiday</DialogTitle><DialogDescription>Books annual leave for the chosen staff and adds it to their timesheets. Anyone without enough leave left gets the extra days as unpaid leave.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="ch-name" placeholder="e.g. Christmas shutdown" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First day</Label><Input data-testid="ch-start" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="mt-1" /></div>
            <div><Label>Last day</Label><Input data-testid="ch-end" type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="mt-1" /></div>
          </div>
          <div><div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Staff categories</div><CategoryPicker cats={cats} value={f.category_names} onChange={(v) => setF({ ...f, category_names: v })} testid="ch-cat" /></div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Individual staff</div>
            <div className="border border-zinc-200 rounded-md p-2 max-h-48 overflow-y-auto space-y-1.5">
              {emps.map((e) => <label key={e.id} className="flex items-center gap-2 text-sm"><Checkbox data-testid={`ch-emp-${e.id}`} checked={f.employee_ids.includes(e.id)} onCheckedChange={() => toggle(e.id)} />{e.first_name} {e.surname}</label>)}
            </div>
          </div>
          <Button data-testid="ch-save" disabled={busy} onClick={save} className="w-full bg-slate-900 text-white">{busy ? "Booking leave…" : "Add holiday"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CompanyHolidaysManager() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/company-holidays").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (h) => {
    if (!window.confirm(`Remove "${h.name}"? The leave will be cancelled for ${h.applied.length} staff and their balances restored.`)) return;
    try { await api.delete(`/company-holidays/${h.id}`); toast.success("Company holiday removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4" data-testid="company-holidays">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">Company holidays</h3>
        <Button data-testid="ch-add-btn" size="sm" variant="outline" onClick={() => setOpen(true)}><CalendarPlus className="h-4 w-4 mr-1" />Add company holiday</Button>
      </div>
      {rows.length === 0 && <p className="text-sm text-zinc-400">No company holidays yet. Add one for shutdowns like Christmas.</p>}
      <div className="divide-y divide-zinc-100">
        {rows.map((h) => {
          const unpaid = h.applied.filter((a) => a.unpaid_days > 0);
          return (
            <div key={h.id} data-testid={`ch-row-${h.id}`} className="py-2.5 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{h.name} <span className="text-zinc-500 font-normal">· {fmtDate(h.start_date)}{h.end_date !== h.start_date ? ` – ${fmtDate(h.end_date)}` : ""} · {h.days} day{h.days === 1 ? "" : "s"}</span></div>
                <div className="text-xs text-zinc-500">{h.applied.length} staff{h.category_names.length ? ` · ${h.category_names.join(", ")}` : ""}</div>
                {unpaid.length > 0 && <div className="text-xs text-amber-700" data-testid={`ch-unpaid-${h.id}`}>Unpaid (not enough leave): {unpaid.map((a) => `${a.name} ${a.unpaid_days}d`).join(", ")}</div>}
                {h.skipped.length > 0 && <div className="text-xs text-zinc-400" data-testid={`ch-skipped-${h.id}`}>Skipped (leave already booked): {h.skipped.map((s) => s.name).join(", ")}</div>}
              </div>
              <button data-testid={`ch-del-${h.id}`} onClick={() => remove(h)} className="text-zinc-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>
      <HolidayDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
